import { API_BASE_URL } from '../config/constants';
import { backofficeAuthService } from './backofficeAuthService';

export interface SignupRequest {
  id: string;
  email: string;
  password_hash: string;
  full_name: string;
  phone: string;
  company_name: string;
  contact_person: string;
  mowilex_customer_id?: string;
  territory: string;
  address1: string;
  address2?: string;
  city: string;
  province: string;
  kecamatan: string;
  kelurahan: string;
  postal_code: string;
  address?: string;
  captcha_token?: string;
  captcha_verified?: boolean;
  status: 'pending' | 'approved' | 'rejected' | 'under_review';
  submitted_at: string;
  reviewed_by?: string;
  reviewed_at?: string;
  admin_notes?: string;
  rejection_reason?: string;
  epicor_customer_id?: string;
  epicor_sync_status?: string;
  epicor_sync_at?: string;
  epicor_sync_error?: string;
  magic_link_token?: string;
  magic_link_expires_at?: string;
  magic_link_sent_at?: string;
  magic_link_sent_via?: string;
  user_id?: string;
  customer_id?: string;
  account_created_at?: string;
  created_at: string;
  updated_at?: string;
}

export interface SignupRequestCreate {
  company_name: string;
  business_email: string;
  contact_person: string;
  contact_email?: string;
  phone?: string;
  business_type?: string;
  tax_id?: string;
  website?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  admin_notes?: string;
  epicor_customer_id?: string;
}

export interface SignupRequestUpdate {
  company_name?: string;
  business_email?: string;
  contact_person?: string;
  contact_email?: string;
  phone?: string;
  business_type?: string;
  tax_id?: string;
  website?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
  admin_notes?: string;
  epicor_customer_id?: string;
}

export interface SignupRequestListResponse {
  items: SignupRequest[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface ApprovalRequest {
  admin_notes?: string;
  send_magic_link?: boolean;
  delivery_method?: 'email' | 'whatsapp' | 'manual';
  custom_message?: string;
}

export interface MagicLink {
  id: string;
  customer_id: string;
  token: string;
  link_url: string;
  delivery_method: string;
  is_used: boolean;
  expires_at: string;
  created_at: string;
  used_at?: string;
}

export interface ActivityLog {
  id: string;
  user_id: string;
  user_name?: string;
  user_email?: string;
  action: string;
  resource_type?: string;
  resource_id?: string;
  details?: any;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

class BackofficeManagementService {
  private baseUrl = `${API_BASE_URL}/backoffice`;

  private async getAuthHeaders() {
    const token = backofficeAuthService.getAccessToken();
    if (!token) {
      throw new Error('No authentication token available');
    }
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      if (response.status === 401) {
        // Try to refresh token
        try {
          await backofficeAuthService.refreshToken();
          throw new Error('Token refreshed, retry request');
        } catch {
          backofficeAuthService.logout();
          throw new Error('Session expired');
        }
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Request failed with status ${response.status}`);
    }
    return response.json();
  }

  // Signup Requests Management
  async getSignupRequests(
    page: number = 1,
    size: number = 20,
    status?: string,
    search?: string
  ): Promise<SignupRequestListResponse> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (status) params.append('status_filter', status);
    if (search) params.append('search', search);

    const response = await fetch(`${this.baseUrl}/signup-requests?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    const result = await this.handleResponse<SignupRequestListResponse>(response);
    
    // Debug: Log the API response to see what's being returned
    console.log('=== DEBUG: getSignupRequests API Response ===');
    console.log('Total items:', result.items.length);
    if (result.items.length > 0) {
      const firstItem = result.items[0];
      console.log('First item:', {
        email: firstItem.email,
        company_name: firstItem.company_name,
        full_name: firstItem.full_name,
        phone: firstItem.phone,
        contact_person: firstItem.contact_person,
        address1: firstItem.address1,
        address2: firstItem.address2,
        city: firstItem.city,
        province: firstItem.province,
        kecamatan: firstItem.kecamatan,
        kelurahan: firstItem.kelurahan,
        postal_code: firstItem.postal_code
      });
    }
    console.log('=======================================');
    
    return result;
  }

  async getSignupRequest(id: string): Promise<SignupRequest> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<SignupRequest>(response);
  }

  async createSignupRequest(data: SignupRequestCreate): Promise<SignupRequest> {
    const response = await fetch(`${this.baseUrl}/signup-requests`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<SignupRequest>(response);
  }

  async updateSignupRequest(id: string, data: SignupRequestUpdate): Promise<SignupRequest> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<SignupRequest>(response);
  }

  async approveSignupRequest(id: string, data: ApprovalRequest): Promise<any> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}/approve`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<any>(response);
  }

  async reviewSignupRequest(id: string, data: {
    status: 'approved' | 'rejected' | 'under_review';
    admin_notes?: string;
    rejection_reason?: string;
    epicor_customer_id?: string;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}/review`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<any>(response);
  }

  async rejectSignupRequest(id: string, reason: string): Promise<any> {
    return this.reviewSignupRequest(id, {
      status: 'rejected',
      rejection_reason: reason
    });
  }

  async verifySignupRequestWithEpicor(id: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}/verify-epicor`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async sendMagicLinkForSignupRequest(id: string, data: {
    delivery_method: 'email' | 'whatsapp' | 'manual';
    regenerate_token?: boolean;
    custom_message?: string;
  }): Promise<any> {
    const response = await fetch(`${this.baseUrl}/signup-requests/${id}/magic-link`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<any>(response);
  }

  // Magic Links Management
  async generateMagicLink(customerId: string, deliveryMethod: string = 'email'): Promise<MagicLink> {
    const response = await fetch(`${this.baseUrl}/magic-links/generate`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({
        customer_id: customerId,
        delivery_method: deliveryMethod,
      }),
    });

    return this.handleResponse<MagicLink>(response);
  }

  async sendMagicLink(linkId: string, deliveryMethod: string): Promise<any> {
    const response = await fetch(`${this.baseUrl}/magic-links/${linkId}/send`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ delivery_method: deliveryMethod }),
    });

    return this.handleResponse<any>(response);
  }

  async getMagicLinks(customerId?: string): Promise<MagicLink[]> {
    const params = new URLSearchParams();
    if (customerId) params.append('customer_id', customerId);

    const response = await fetch(`${this.baseUrl}/magic-links?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<MagicLink[]>(response);
  }

  // Activity Logs
  async getActivityLogs(
    page: number = 1,
    size: number = 20,
    actionFilter?: string,
    userId?: string
  ): Promise<{logs: ActivityLog[], total: number, page: number, size: number, pages: number}> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (actionFilter) params.append('action_filter', actionFilter);
    if (userId) params.append('user_id', userId);

    const response = await fetch(`${this.baseUrl}/activity-logs?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Dashboard Stats
  async getDashboardStats(): Promise<{
    total_signup_requests: number;
    pending_requests: number;
    approved_requests: number;
    rejected_requests: number;
    recent_activity: ActivityLog[];
  }> {
    const response = await fetch(`${this.baseUrl}/dashboard/stats`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Dashboard Analytics with date range
  async getDashboardAnalytics(
    startDate?: string,
    endDate?: string
  ): Promise<{
    date_range: {
      start_date: string;
      end_date: string;
    };
    signup_requests: Array<{ date: string; value: number }>;
    customer_orders: Array<{ date: string; value: number }>;
    customer_signins: Array<{ date: string; value: number }>;
    sales_trend: Array<{ date: string; orders: number; sales: number }>;
  }> {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await fetch(`${this.baseUrl}/dashboard/analytics?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Customer Management
  async getCustomers(
    page: number = 1,
    size: number = 20,
    search?: string,
    status?: string,
    province?: string,
    city?: string,
    portalStatus?: string,
    sortBy?: string,
    sortOrder?: string,
    territory?: string,
    customerGroup?: string
  ): Promise<{
    items: any[];
    total: number;
    page: number;
    size: number;
    pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    if (search) params.append('search', search);
    if (status) params.append('status', status);
    if (province) params.append('province', province);
    if (city) params.append('city', city);
    if (portalStatus) params.append('portal_status', portalStatus);
    if (sortBy) params.append('sort_by', sortBy);
    if (sortOrder) params.append('sort_order', sortOrder);
    if (territory) params.append('territory', territory);
    if (customerGroup) params.append('customer_group', customerGroup);

    const response = await fetch(`${this.baseUrl}/customers?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  // Get filter options (enhanced with territories and customer groups)
  async getCustomerFilterOptions(): Promise<{
    provinces: string[];
    cities: string[];
    territories: string[];
    customer_groups: string[];
  }> {
    const response = await fetch(`${this.baseUrl}/customers/filter-options`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<{
      provinces: string[];
      cities: string[];
      territories: string[];
      customer_groups: string[];
    }>(response);
  }

  // Activate customer (create portal user)
  async activateCustomer(customerId: string, data: {
    email: string;
    full_name: string;
    phone?: string;
    delivery_method: 'email' | 'whatsapp' | 'manual';
    custom_message?: string;
    admin_notes?: string;
  }): Promise<{
    success: boolean;
    message: string;
    customer_id: string;
    user_id: string;
    temporary_password: string;
    activation_url: string;
    activation_token: string;
    email_sent: boolean;
  }> {
    const response = await fetch(`${this.baseUrl}/customers/${customerId}/activate`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<any>(response);
  }

  async resendActivation(customerId: string, deliveryMethod: string = 'email'): Promise<{
    success: boolean;
    message: string;
    activation_url: string;
    email_sent: boolean;
    delivery_method: string;
  }> {
    const response = await fetch(`${this.baseUrl}/customers/${customerId}/resend-activation`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ delivery_method: deliveryMethod }),
    });

    return this.handleResponse<any>(response);
  }

  async changeMainContact(customerId: string, data: {
    email: string;
    full_name: string;
    phone?: string;
  }): Promise<{
    success: boolean;
    message: string;
    user_id: string;
    activation_url: string;
    email_sent: boolean;
  }> {
    const response = await fetch(`${this.baseUrl}/customers/${customerId}/main-contact`, {
      method: 'PUT',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify(data),
    });

    return this.handleResponse<any>(response);
  }

  async getCustomerOrders(
    customerId: string,
    page: number = 1,
    size: number = 50
  ): Promise<any[]> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });

    const response = await fetch(`${this.baseUrl}/customers/${customerId}/orders?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any[]>(response);
  }

  // Invoice Management
  async getCustomerInvoices(
    customerId: string,
    page: number = 1,
    size: number = 20,
    statusFilter?: string
  ): Promise<{
    items: any[];
    total: number;
    page: number;
    size: number;
    pages: number;
  }> {
    const params = new URLSearchParams({
      page: page.toString(),
      size: size.toString(),
    });
    
    if (statusFilter && statusFilter !== 'all') {
      params.append('status_filter', statusFilter);
    }

    const response = await fetch(`${this.baseUrl}/customers/${customerId}/invoices?${params}`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async getOrderInvoice(orderId: string): Promise<{
    success: boolean;
    message: string;
    invoice_id?: string;
    invoice_number?: number;
    pdf_path?: string;
    download_url?: string;
    error?: string;
  }> {
    const response = await fetch(`${this.baseUrl}/orders/${orderId}/invoice`, {
      headers: await this.getAuthHeaders(),
    });

    return this.handleResponse<any>(response);
  }

  async generateOrderInvoice(
    orderId: string,
    forceRegenerate: boolean = false
  ): Promise<{
    success: boolean;
    message: string;
    invoice_id?: string;
    invoice_number?: number;
    pdf_path?: string;
    download_url?: string;
    error?: string;
  }> {
    const response = await fetch(`${this.baseUrl}/orders/${orderId}/invoice/generate`, {
      method: 'POST',
      headers: await this.getAuthHeaders(),
      body: JSON.stringify({ force_regenerate: forceRegenerate }),
    });

    return this.handleResponse<any>(response);
  }

  async downloadInvoice(invoiceId: string): Promise<void> {
    const headers = await this.getAuthHeaders();
    const url = `${this.baseUrl}/invoices/${invoiceId}/download`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to download invoice: ${response.statusText}`);
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    
    // Open in new tab instead of downloading
    window.open(downloadUrl, '_blank');
    
    // Clean up the URL after a delay to ensure it loads
    setTimeout(() => {
      window.URL.revokeObjectURL(downloadUrl);
    }, 1000);
  }

  getInvoiceDownloadUrl(invoiceId: string): string {
    const token = backofficeAuthService.getAccessToken();
    return `${this.baseUrl}/invoices/${invoiceId}/download?token=${token}`;
  }

  // ──────────────────────────────────────────────────────────────
  // Zegen DMS documents — signed invoice, delivery notes, faktur
  // pajak. Backoffice consumes the same response shape as the
  // customer surface (see frontend/src/services/invoiceService.ts
  // DmsDocumentResponse) so the shared DmsDocumentsPanel component
  // renders identically for both audiences.
  // ──────────────────────────────────────────────────────────────

  /**
   * Fetch DMS document metadata for an invoice.
   * `refresh=true` forces the backend to re-call Zegen (backoffice-
   * only option -- useful when the operator knows a document has
   * just landed on Zegen and doesn't want to wait for the 1-hour
   * cache to go stale).
   */
  async getInvoiceDmsDocuments(
    invoiceId: string,
    refresh: boolean = false,
  ): Promise<import('./invoiceService').DmsDocumentResponse> {
    const headers = await this.getAuthHeaders();
    const url = `${this.baseUrl}/invoices/${invoiceId}/dms-documents${refresh ? '?refresh=1' : ''}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      throw new Error(`Failed to load DMS documents: ${response.statusText}`);
    }
    return response.json();
  }

  /**
   * Download a specific DMS document (signed invoice, delivery note,
   * or faktur pajak). Fetches the PDF bytes with auth headers and
   * opens the blob in a new tab (same pattern as downloadInvoice).
   */
  async downloadDmsDocument(
    invoiceId: string,
    docType: 'signed-invoice' | 'delivery-note' | 'faktur-pajak',
    docIndex: number = 0,
  ): Promise<void> {
    const headers = await this.getAuthHeaders();
    const url = `${this.baseUrl}/invoices/${invoiceId}/dms-documents/proxy/${docType}/${docIndex}`;
    const response = await fetch(url, { method: 'GET', headers });
    if (!response.ok) {
      // Try to surface the JSON detail if the backend returned one.
      let detail = response.statusText;
      try {
        const body = await response.json();
        if (typeof body?.detail === 'string') detail = body.detail;
      } catch {
        // Not JSON; keep statusText.
      }
      throw new Error(`Failed to download document: ${detail}`);
    }
    const blob = await response.blob();
    const objectUrl = window.URL.createObjectURL(blob);
    window.open(objectUrl, '_blank');
    setTimeout(() => window.URL.revokeObjectURL(objectUrl), 1000);
  }
}

export const backofficeManagementService = new BackofficeManagementService();